import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { DataTableAdvanced, type DataTableColumn } from '@/components/ui/data-table-advanced'
import { apiService } from '@/services/api'
import { Plus, MoreHorizontal, Edit, Trash2 } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { format } from 'date-fns'
import { useDrawer } from '@/components/ui/drawer'
import { useDrawerMessaging } from '@/hooks/useDrawerMessaging'
import { toast } from 'sonner'
import { BlogCategoryForm } from '@/components/blog/BlogCategoryForm'

interface BlogCategory {
  id: string
  name: string
  slug: string
  description?: string
  color?: string
  created_at: string
}

export const BlogCategories: React.FC = () => {
  const { openDrawer, closeDrawer } = useDrawer()
  const { onMessage } = useDrawerMessaging('blog-categories-page')
  const [categories, setCategories] = useState<BlogCategory[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchCategories()
  }, [])

  useEffect(() => {
    return onMessage((message) => {
      if (message.action === 'refresh') {
        fetchCategories()
      }
    })
  }, [onMessage])

  const fetchCategories = async () => {
    try {
      setLoading(true)
      const data = await apiService.blog.categories.list()
      setCategories(data)
    } catch (err) {
      console.error('Failed to fetch blog categories:', err)
      toast.error('Failed to load article categories')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateCategory = () => {
    const drawerId = 'create-blog-category'
    openDrawer({
      id: drawerId,
      title: 'Create Article Category',
      content: <BlogCategoryForm
        onSuccess={() => {
          closeDrawer(drawerId)
          fetchCategories()
          toast.success('Category created successfully')
        }}
      />,
      footer: null,
      size: 'md',
    })
  }

  const handleEditCategory = (category: BlogCategory) => {
    const drawerId = `edit-blog-category-${category.id}`
    openDrawer({
      id: drawerId,
      title: `Edit: ${category.name}`,
      content: <BlogCategoryForm
        category={category}
        onSuccess={() => {
          closeDrawer(drawerId)
          fetchCategories()
          toast.success('Category updated successfully')
        }}
      />,
      footer: null,
      size: 'md',
    })
  }

  const handleDeleteCategory = async (category: BlogCategory) => {
    if (!confirm(`Are you sure you want to delete "${category.name}"?`)) {
      return
    }

    try {
      await apiService.blog.categories.delete(category.id)
      toast.success('Article category deleted successfully')
      fetchCategories()
    } catch (err) {
      console.error('Failed to delete blog category:', err)
      toast.error('Failed to delete article category')
    }
  }

  const columns: DataTableColumn<BlogCategory>[] = [
    {
      id: 'name',
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          {row.original.color && (
            <div
              className="w-4 h-4 rounded-full ring-1 ring-border"
              style={{ backgroundColor: row.original.color }}
            />
          )}
          <div>
            <div className="font-medium">{row.original.name}</div>
            <div className="text-sm text-muted-foreground">/{row.original.slug}</div>
          </div>
        </div>
      ),
    },
    {
      id: 'description',
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => (
        <div className="max-w-xs">
          {row.original.description ? (
            <div className="text-sm text-muted-foreground truncate">
              {row.original.description}
            </div>
          ) : (
            <span className="text-muted-foreground italic">No description</span>
          )}
        </div>
      ),
    },
    {
      id: 'created_at',
      accessorKey: 'created_at',
      header: 'Created',
      cell: ({ row }) => (
        <div className="text-sm text-muted-foreground">
          {format(new Date(row.original.created_at), 'MMM d, yyyy')}
        </div>
      ),
    },
    {
      id: 'actions',
      accessorKey: 'id',
      header: '',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => handleEditCategory(row.original)}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => handleDeleteCategory(row.original)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Article Categories</h1>
          <p className="text-muted-foreground">
            Organize your articles with categories
          </p>
        </div>
        <Button onClick={handleCreateCategory} className="gap-2">
          <Plus className="h-4 w-4" />
          New Category
        </Button>
      </div>

      {/* Data Table */}
      <DataTableAdvanced
        data={categories}
        columns={columns}
        loading={loading}
        searchable={true}
        searchKeys={['name', 'description']}
        searchPlaceholder="Search categories..."
        emptyMessage="No article categories found"
      />
    </div>
  )
}

export default BlogCategories